import {
	apiPost,
	getApiUrl,
	type PostOrdersIdPhotosFetchResponse,
	postOrdersIdPhotosPath,
} from "Infrastructure/Api/Api";
import {
	ApiCallError,
	getMessageFromErrorResponse,
	processResponse,
} from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { getOrderPhotosQueryKey } from "Order/Api/Query/useOrderPhotosQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const orderMutationsResource = Resources.base.order.mutations;

/**
 * Bypasses the generated `postOrdersIdPhotos`: its FormData serializer
 * JSON-stringifies `File[]` unless the key ends in `[]`, while the backend
 * expects repeated `photos` parts.
 */
const postOrderPhotos = (orderId: number, files: File[]) => {
	const formData = new FormData();
	for (const file of files) {
		formData.append("photos", file);
	}
	return apiPost(
		`${getApiUrl()}${postOrdersIdPhotosPath(orderId)}`,
		formData,
		new Headers(),
	) as Promise<PostOrdersIdPhotosFetchResponse>;
};

export const useUploadOrderPhotosMutation = (orderId: number) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse((files: File[]) =>
			postOrderPhotos(orderId, files),
		),
		onError: (error) => {
			console.error(
				`${t(orderMutationsResource.orderPhotosUploadError)}:`,
				error,
			);
			// The cached photo count may be stale (another admin filled a slot);
			// refetch so `remaining` reflects the server.
			queryClient.invalidateQueries({
				queryKey: getOrderPhotosQueryKey(orderId),
			});
			if (!(error instanceof ApiCallError)) {
				return;
			}
			// The global mutation error handler suppresses 400/401 toasts, so
			// surface those here; every other status is already toasted globally.
			const { status } = error.data;
			if (status !== 400 && status !== 401) {
				return;
			}
			toast.error(
				`${t(orderMutationsResource.orderPhotosUploadError)}: ${getMessageFromErrorResponse(error)}`,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: getOrderPhotosQueryKey(orderId),
			});
			toast.success(t(orderMutationsResource.orderPhotosUploadSuccess));
		},
	});
};
